import { Tooltip } from 'chart.js'
import dayjs from 'dayjs'
import { cloneDeep, isEmpty, isNumber, merge } from 'lodash'

import {
  dateFormat,
  getLabelForQuarter,
  MILESTONE_DAY_FORMAT,
  MILESTONE_MONTH_FORMAT
} from '@/utils/date'
import { memoizeGetCssVariableValue } from '@/utils/memoizations'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import {
  DEFAULT_RESULT_TYPE_VALUES,
  isThresholdProgress,
  THRESHOLD_CONDITIONS
} from '@/utils/okr-element-values'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'
import { THRESHOLDS_DEFAULT_VALUES } from '@/utils/thresholds'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

const PROGRESS_CHART_TOOLTIP_POSITIONER = 'progressChartTooltipPositioner'

// Create a custom tooltip positioner to put at the bottom of the chart area
Tooltip.positioners[PROGRESS_CHART_TOOLTIP_POSITIONER] = items => {
  const pos = Tooltip.positioners.average(items)

  // Happens when nothing is found
  if (pos === false) {
    return false
  }

  return {
    x: pos.x,
    y: 0,
    xAlign: 'center',
    yAlign: 'bottom'
  }
}

const systemUiFontFamily =
  "'-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'sans-serif'"
export const CHART_TYPES = {
  simple: 'simple',
  simpleNext: 'simple-next',
  detailed: 'detailed',
  detailedNext: 'detailed-next',
  dashboardPreview: 'dashboardPreview',
  homepagePreview: 'homepagePreview',
  predictedScorePreview: 'predictedScorePreview',
  elementProgress: 'elementProgress'
}

export const FILL_STRATEGIES = {
  START: 'start',
  END: 'end',
  TRUE: true,
  FALSE: false
}

export const getResolvedUnitForChart = (unit = '') => {
  // add space before unit if it's not a percentage
  return unit.replace(new RegExp(`^([^${MATH_SYMBOLS.PERCENT}])`), ' $1')
}

export const getTargetLinesFillStrategy = ({
  fieldTypeId = KR_VALUE_TYPES.START_TARGET,
  thresholdConditionTypeId = DEFAULT_RESULT_TYPE_VALUES.thresholdConditionTypeId
} = {}) => {
  if (
    isThresholdProgress({
      element: {
        fieldTypeId
      }
    })
  ) {
    const isAboveCondition = [
      THRESHOLD_CONDITIONS.ABOVE.value,
      THRESHOLD_CONDITIONS.ABOVE_OR_EQUAL.value
    ].includes(thresholdConditionTypeId)

    return isAboveCondition ? FILL_STRATEGIES.START : FILL_STRATEGIES.END
  }

  return FILL_STRATEGIES.FALSE
}

export const TIME_INTERVALS_COUNT = 12
export const AGGREGATED_INTERVALS_COUNT = 12

export const TRANSPARENT_COLOR = 'transparent'

export const LINEAR_LABEL = 'Linear'

export const THRESHOLD_LABEL = 'Threshold'

export const TARGET_PERIODS_LABEL = 'Target-periods'

export const ELEMENT_PROGRESS_LABEL = 'Element-progress'

const DEFAULT_GRID_COLOR = memoizeGetCssVariableValue('--grey-2-next', '#DFE1E6')
/**
 * @type String
 */
export const DEFAULT_CHART_LINE_COLOR = memoizeGetCssVariableValue(
  '--primary-color-next',
  '#0052cc'
)

const GREY_3_NEXT_COLOR = memoizeGetCssVariableValue('--grey-3-next', '#F5F6F7')

const GREY_1_NEXT_COLOR = memoizeGetCssVariableValue('--grey-1-next', '#B5BBC5')

const DARK_3_COLOR = memoizeGetCssVariableValue('--dark-3', '#6B778C')

const WHITE_COLOR = memoizeGetCssVariableValue('--white-color', '#fff')

const TICKS_ALIGNS = {
  INNER: 'inner'
  // ↑ ALIGN TICK TO GRID LINE
  // EXAMPLES

  // INNER
  // 100% ————————————
  //
  // 50% ————————————
  //
  // 0% ————————————

  // START
  //     _______________
  // 100%
  //
  //     _______________
  // 50%
  //
  //     _______________
  // 0%

  // END
  // 100% _______________
  //
  // 50% _______________
  //
  // 0% _______________
  // https://www.chartjs.org/docs/latest/api/interfaces/TickOptions.html
}

export const TARGET_LINES_COLOR = '#FDA170'

export const PERIODS = {
  DEFAULT: {
    value: null,
    unit: 'day',
    format: 'DD/MMM/YYYY'
  },
  DAILY: {
    value: 1,
    unit: 'day',
    format: 'DD/MMM'
  },
  WEEKLY: {
    value: 2,
    unit: 'week',
    format: 'DD/MMM'
  },
  MONTHLY: {
    value: 3,
    unit: 'month',
    format: 'MMM/YYYY'
  },
  QUARTERLY: {
    value: 4,
    unit: 'quarter',
    format: 'MMM/YYYY'
  },
  YEARS: {
    value: 5,
    unit: 'year',
    format: 'MMM/YYYY'
  }
}

export const ELEMENT_PROGRESS_CHART_TYPE_IDS = {
  VALUES: 1,
  PROGRESS: 2
}

const DEFAULT_TOOLTIP_OPTIONS = {
  mode: 'nearest',
  intersect: false,
  callbacks: {
    label: ({ dataset, formattedValue }) => {
      const label = dataset.label
      return `${
        label.length > 30 ? label.substring(0, 30).concat('...') : label
      }: ${formattedValue}${MATH_SYMBOLS.PERCENT}`
    }
  },
  filter: ({ dataset }) => {
    return ![LINEAR_LABEL, TARGET_PERIODS_LABEL].includes(dataset.label)
  }
}

const getStyledTooltipSettings = ({ yMax = 100, unit = '' } = {}) => {
  return {
    caretPadding: 10,
    caretSize: 4,
    backgroundColor: DEFAULT_CHART_LINE_COLOR,
    cornerRadius: 12,
    displayColors: false,
    bodyAlign: 'center',
    titleAlign: 'center',

    titleFont: {
      size: 12,
      weight: '400',
      family: systemUiFontFamily
    },
    bodyFont: {
      size: 20,
      weight: '600',
      family: systemUiFontFamily
    },
    padding: {
      x: 16,
      y: 10
    },
    yAlign: ({ tooltip }) => {
      const { dataPoints } = tooltip

      if (isEmpty(dataPoints)) {
        return 'bottom'
      }

      const currentPointY = dataPoints[0].parsed.y

      if (currentPointY > yMax / 2) {
        return 'top'
      }

      return 'bottom'
    },
    callbacks: {
      title: function (elements) {
        if (isEmpty(elements)) return null
        return dayjs(elements[0].parsed.x).format(dateFormat)
      },
      label: function (elements) {
        return `${
          getSimplifiedNumber(elements.parsed.y, true)[PLURALIZATION_KEYS.SIMPLIFIED]
        }${unit}`
      }
    }
  }
}

const TOOLTIP_INTERACTION_MODE_SETTINGS = {
  intersect: false,
  mode: 'index'
}

const FONT_SETTINGS = {
  size: 10,
  family: systemUiFontFamily
}

const getSimplifiedYTick = ({ value = 100, unit = '', skipEvenPoints = true } = {}) => {
  const simplified = `${getSimplifiedNumber(value, true)[PLURALIZATION_KEYS.SIMPLIFIED]}${unit}`

  if (skipEvenPoints) {
    return value % 20 === 0 ? simplified : null
  }

  return simplified
}

// TEST CASE
/*
;[
  800,
  0.1,
  0.001,
  0.0001,
  0.00001,
  ...Array(101).keys(),
  111,
  222,
  333,
  444,
  555,
  666,
  777,
  888,
  999,
  1000,
  42137,
  112456,
  7894561,
  12365785
]
  .flatMap(n => [n, n * -1])
  .forEach(n => {
    console.log({
      n,
      result: roundByModulo(n)
    })
  })
 */

const getDigitOfNumber = (num = 0) => {
  // return Math.pow(10, Math.trunc(Math.log10(num)))
  // this solution has bad precision for small numbers like 0.0001
  // or big numbers like 1797987889.123456789,
  // so I create own solution
  return Number(`1e${Math.trunc(Math.abs(num)).toString().length - 1}`)
}

const roundByModulo = (num = 0) => {
  if (!num) return 0

  const absNum = Math.abs(num)

  const [hardRoundedNum] = [
    absNum >= 0 && absNum < 10 && 10,
    absNum >= 10 && absNum < 100 && 100
  ].filter(Boolean)

  const multiplier = Math.sign(num)

  if (hardRoundedNum) {
    return hardRoundedNum * multiplier
  }

  const digitCapacity = getDigitOfNumber(num)

  if (num % digitCapacity === 0) {
    return num + digitCapacity * multiplier
  }

  const roundFn = multiplier > 0 ? Math.ceil : Math.floor

  return roundFn(num / digitCapacity) * digitCapacity
}

const getChartLinesDataPoints = chartLines => {
  return {
    yPoints: chartLines.flatMap(cl => Object.values(cl.data)),
    xPoints: chartLines.flatMap(cl => Object.keys(cl.data))
  }
}

const getMinMax = (chartLines, fullSizeChart) => {
  const { yPoints, xPoints } = getChartLinesDataPoints(chartLines)
  if (fullSizeChart) {
    return {
      xMin: Math.min(...xPoints),
      xMax: Math.max(...xPoints),
      yMin: Math.min(...yPoints),
      yMax: Math.max(...yPoints)
    }
  } else {
    const isSomeYEqual100 = yPoints.some(cl => cl === 100)
    return chartLines.reduce(
      (acc, cl) => {
        return {
          xMin: Math.min(acc.xMin, cl.xmin),
          xMax: Math.max(acc.xMax, cl.xmax),
          yMin: Math.min(acc.yMin, cl.ymin),
          yMax: isSomeYEqual100 ? 150 : roundByModulo(Math.max(acc.yMax, cl.ymax))
        }
      },
      {
        xMin: Infinity,
        xMax: -Infinity,
        yMin: Infinity,
        yMax: -Infinity
      }
    )
  }
}

const convertDatasetData = (item, tableData = {}) => {
  return Object.entries(item.data).map(([x, y]) => ({
    x: Number(x),
    y,
    ...tableData
  }))
}

const prepareDatasets = (
  chartLines,
  splitEnabled,
  pointBackgroundColor,
  pointHoverBackgroundColor,
  tableData,
  tension = 0.4,
  targetLines = [],
  chartType = CHART_TYPES.simple,
  targetLinesOptions = {
    fill: false
  }
) => {
  const chartLinesDataSets = chartLines
    .map(item => {
      const label = chartType === CHART_TYPES.elementProgress ? ELEMENT_PROGRESS_LABEL : item.name

      return {
        label,
        id: item.id,
        borderColor: item.color || DEFAULT_CHART_LINE_COLOR,
        data: convertDatasetData(item, tableData),
        tension,
        pointBackgroundColor: pointBackgroundColor || item.color,
        pointHoverBackgroundColor: pointHoverBackgroundColor || item.color,
        borderCapStyle: 'round'
      }
    })
    .filter(item => {
      return !isEmpty(item.data)
    })

  const targetLinesDataSets = targetLines.map(item => {
    return {
      borderCapStyle: 'round',
      borderColor: TARGET_LINES_COLOR,
      borderWidth: 1,
      label: TARGET_PERIODS_LABEL,
      data: convertDatasetData(item),
      tension,
      pointRadius: 0,
      pointHoverRadius: 0,
      borderDash: [5, 3],
      fill: targetLinesOptions.fill || false
    }
  })

  return [...chartLinesDataSets, ...targetLinesDataSets]
}

const prepareLinear = chartLines => {
  const { xMin, xMax, yMin } = getMinMax(chartLines)
  return {
    borderColor: GREY_1_NEXT_COLOR,
    borderWidth: 0.3,
    pointRadius: 0,
    borderDash: [10, 3],
    label: LINEAR_LABEL,
    data: [
      { x: xMin, y: yMin },
      { x: xMax, y: 100 }
    ]
  }
}

const prepareThresholds = ({
  thresholdBehind = THRESHOLDS_DEFAULT_VALUES.BEHIND,
  thresholdOnTrack = THRESHOLDS_DEFAULT_VALUES.ON_TRACK,
  chartLines,
  fillThresholdLines = false
}) => {
  const { xMax, xMin } = getMinMax(chartLines, true)

  const fill = fillThresholdLines

  const THRESHOLD_LINE_DEFAULT_OPTIONS = {
    borderWidth: 1,
    pointRadius: 0,
    borderDash: fill ? undefined : [4, 4],
    fill,
    label: THRESHOLD_LABEL
  }

  const THRESHOLD_LINES = [
    {
      backgroundColor: '#FDDBE0',
      borderColor: memoizeGetCssVariableValue('--grade-low-color-rgb-next', '246, 73, 99'),
      y2: thresholdBehind
    },
    {
      backgroundColor: '#FFE7D9',
      borderColor: memoizeGetCssVariableValue('--grade-medium-color-rgb-next', '255, 133, 65'),

      y2: thresholdOnTrack
    },
    {
      backgroundColor: '#D7F0E5',
      borderColor: memoizeGetCssVariableValue('--grade-high-color-rgb-next', '54, 178, 126'),

      y2: 100
    }
  ]

  return THRESHOLD_LINES.map(({ borderColor, backgroundColor, y2 }) => {
    return {
      ...THRESHOLD_LINE_DEFAULT_OPTIONS,
      borderColor: fill ? backgroundColor : `rgb(${borderColor}, 0.5)`,
      backgroundColor: backgroundColor,
      data: [
        { x: xMin, y: 0 },
        { x: xMax, y: y2 }
      ]
    }
  })
}

const getTitle = ({ startDate, endDate, dateFormat, isSingleDateFormat = true }) => {
  return isSingleDateFormat
    ? `${dayjs(startDate).utc().format(dateFormat)}`
    : `${dayjs(startDate).utc().format(dateFormat)} - ${dayjs(endDate).utc().format(dateFormat)}`
}

export const CHECKER_COLUMN_WIDTH = 24
export const MARKER_COLUMN_WIDTH = 24

const prepareColumns = (columns, granulation) => {
  const defaultColumns = [
    {
      key: 'marker',
      width: MARKER_COLUMN_WIDTH
    },
    {
      key: 'checker',
      width: CHECKER_COLUMN_WIDTH
    },
    {
      title: 'Name',
      key: 'name',
      width: 276
    }
  ]

  const restColumns = columns.map(column => {
    const { startDate, endDate } = column
    const isSingleDateFormat =
      granulation === PERIODS.DAILY.value || granulation === PERIODS.MONTHLY.value
    const { format } = Object.values(PERIODS).find(({ value }) => value === granulation)
    return {
      title: getTitle({ startDate, endDate, dateFormat: format, isSingleDateFormat }),
      key: endDate,
      width: 110,
      align: 'left'
    }
  })

  return [...defaultColumns, ...restColumns]
}

const prepareRows = source => {
  return source.chartLines.map((chartLine, index) => {
    const entries = source.tableData.columns.map((column, columnIndex) => {
      const value = source.tableData.data[index][columnIndex]
      const newValue = isNumber(value) ? value : '—'
      return [column.endDate, newValue]
    })
    const { name, color, id, displayId, levelColor, levelPrefix, issueIcon, typeId, permissions } =
      chartLine
    return {
      permissions,
      name,
      color,
      id,
      displayId,
      levelColor,
      levelPrefix,
      issueIcon,
      typeId,
      ...Object.fromEntries(entries)
    }
  })
}

const prepareLabels = ({
  chartLines,
  fullSizeChart,
  timeIntervalsCount,
  granulation,
  scales = {}
}) => {
  const predefinedXTicks = scales?.x || []

  if (!isEmpty(predefinedXTicks)) {
    return [...predefinedXTicks]
  }

  if (chartLines.length === 0) {
    return []
  }

  let interval = timeIntervalsCount
  const { xMin: min, xMax: max } = getMinMax(chartLines, fullSizeChart)
  const step = (max - min) / (interval - 1)

  if (granulation) {
    const period = Object.values(PERIODS).find(({ value }) => value === granulation)

    const dateMax = dayjs(max)
    const dateMin = dayjs(min)
    interval = dateMax.diff(dateMin, period.unit)
    return Array.from({ length: interval + 2 }, (value, index) => {
      const date = dayjs(min).add(index, period.unit)

      if (period.value === PERIODS.QUARTERLY.value) {
        return `${dayjs(date).startOf('quarters')}`
      }

      return `${date}`
    })
  }

  return Array.from({ length: interval }, (value, index) => {
    if (index === interval) {
      return max
    }
    const val = min + index * step
    return val >= max ? max : val
  })
}

const getDefaultOptions = (chartLines, fullSizeChart, aspectRatio, setYFrom0To100 = false) => {
  const checkBeginAtZeroNeed = () => {
    const { yPoints } = getChartLinesDataPoints(chartLines)
    return setYFrom0To100 || yPoints.filter(point => point !== null).every(point => point === 0)
  }
  const beginAtZero = fullSizeChart ? checkBeginAtZeroNeed() : true
  return {
    elements: {
      line: {
        borderWidth: 2
      }
    },
    aspectRatio,
    pointRadius: 3,
    scales: {
      x: {
        type: 'time',
        ticks: {
          source: 'labels'
        },
        grid: {
          display: true
        }
      },
      y: {
        beginAtZero,
        grid: {
          display: true
        }
      }
    },
    clip: false
  }
}

const getMinMaxOptions = (chartLines, fullSizeChart) => {
  const options = {
    scales: {
      y: {
        max: 0
      }
    }
  }
  if (chartLines.length) {
    const { yMax } = getMinMax(chartLines, fullSizeChart)
    options.scales.y.max = yMax
  }
  return options
}

const getMinMaxOptionsForFullSizeChart = (
  chartLines,
  setYFrom0To100,
  roundYMaxToBigger = false
) => {
  const options = {
    scales: {
      y: {
        max: 100
      }
    }
  }

  if (chartLines.length) {
    const { yMax } = getMinMax(chartLines, true)
    if (yMax > 100) {
      options.scales.y.max = roundYMaxToBigger ? roundByModulo(yMax) : yMax
    }
  }
  return setYFrom0To100 ? options : {}
}

const getSimpleChartOptions = ({
  chartLines,
  fullSizeChart,
  splitEnabled,
  aspectRatio,
  setYFrom0To100
}) => {
  const defaultOptions = getDefaultOptions(chartLines, fullSizeChart, aspectRatio, setYFrom0To100)
  const minMaxOptions = fullSizeChart
    ? getMinMaxOptionsForFullSizeChart(chartLines, setYFrom0To100, true)
    : getMinMaxOptions(chartLines, fullSizeChart)

  const getPointRadius = () => {
    if (splitEnabled) {
      return 2
    } else if (isEmpty(chartLines)) {
      return 0
    } else {
      return Object.keys(chartLines[0].data).length === 1 ? 2 : 0
    }
  }

  const options = {
    elements: {
      line: {}
    },
    pointRadius: getPointRadius(),

    scales: {
      x: {
        display: false
      },
      y: {
        display: fullSizeChart
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      }
    }
  }

  return merge(defaultOptions, minMaxOptions, options)
}

const getSimpleNextChartOptions = ({
  chartLines,
  fullSizeChart,
  splitEnabled,
  aspectRatio,
  setYFrom0To100
}) => {
  const defaultOptions = getDefaultOptions(chartLines, fullSizeChart, aspectRatio, setYFrom0To100)
  const minMaxOptions = fullSizeChart
    ? getMinMaxOptionsForFullSizeChart(chartLines, setYFrom0To100, true)
    : getMinMaxOptions(chartLines, fullSizeChart)

  const DOT_RADIUS = 4.5 // dot is 6 px diameter so radius is 3 px, border width is 3px so radius of border is 1.5 px
  const DOT_DIAMETER = DOT_RADIUS * 2
  const chartLinesCount = isEmpty(chartLines) ? 0 : Object.keys(chartLines[0].data).length

  const getPointRadius = () => {
    if (splitEnabled) {
      return 2
    } else {
      return [...Array(chartLinesCount).keys()].map(i => {
        return i + 1 === chartLinesCount ? DOT_RADIUS : 0
      })
    }
  }

  const options = {
    elements: {
      line: {
        borderWidth: 6
      }
    },
    pointRadius: getPointRadius(),
    pointHoverRadius: getPointRadius(),
    pointBorderWidth: 3,

    layout: {
      autoPadding: chartLinesCount <= 1,
      // necessary because `chart.js` adds a padding to center the chart.
      // but if we hide all dots except the last one, line chart keep being centered and start
      // not from canvas border. so we remove all paddings except left and add rest padding
      // equals to point diameter for prevent point cropping
      padding:
        chartLinesCount === 1
          ? DOT_DIAMETER
          : { top: DOT_DIAMETER, right: DOT_DIAMETER, bottom: DOT_DIAMETER }
    },

    scales: {
      x: {
        display: false
      },
      y: {
        display: false //fullSizeChart
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      }
    }
  }

  return merge(defaultOptions, minMaxOptions, options)
}

const getDetailedChartOptions = ({ chartLines, fullSizeChart, aspectRatio }) => {
  const defaultOptions = getDefaultOptions(chartLines, fullSizeChart, aspectRatio)
  const minMaxOptions = fullSizeChart ? {} : getMinMaxOptions(chartLines, fullSizeChart)
  const { yMax } = getMinMax(chartLines, fullSizeChart)

  const options = {
    plugins: {
      tooltip: {
        ...DEFAULT_TOOLTIP_OPTIONS,
        callbacks: {
          ...DEFAULT_TOOLTIP_OPTIONS.callbacks,
          title: data => {
            if (data?.length) {
              const [{ raw }] = data
              const { format } =
                Object.values(PERIODS).find(({ value }) => value === raw.granulation) ||
                PERIODS.DEFAULT

              const granulation = raw.granulation
              const isSingleDateFormat =
                granulation === PERIODS.DAILY.value || granulation === PERIODS.MONTHLY.value
              const tableColumn = raw.columns?.find(
                ({ startDate, endDate }) => startDate <= raw.x && endDate >= raw.x
              )

              return getTitle({
                startDate: tableColumn?.startDate || raw.x,
                endDate: tableColumn?.endDate,
                dateFormat: format,
                isSingleDateFormat
              })
            } else {
              return null
            }
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          stepSize: calculateYAxesStepSize(yMax),
          font: {
            family: systemUiFontFamily
          }
        }
      }
    }
  }

  return merge(defaultOptions, minMaxOptions, options)
}

const getDetailedNextChartOptions = ({ chartLines, fullSizeChart, aspectRatio, splitEnabled }) => {
  const clone = cloneDeep(
    getDetailedChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio
    })
  )

  clone.elements.line.borderWidth = splitEnabled ? 2 : 6
  clone.pointRadius = splitEnabled ? 3 : 4.5
  clone.pointBorderWidth = splitEnabled ? 0 : 3
  clone.scales.y.grid = {
    ...clone.scales.y.grid,
    borderDash: [4, 2],
    tickColor: TRANSPARENT_COLOR,
    tickLength: 20,
    color: DEFAULT_GRID_COLOR,
    borderColor: DEFAULT_GRID_COLOR
  }

  clone.scales.x.grid = {
    ...clone.scales.x.grid,
    tickColor: TRANSPARENT_COLOR,
    color: DEFAULT_GRID_COLOR,
    borderColor: DEFAULT_GRID_COLOR
  }
  clone.scales.y.ticks = {
    ...clone.scales.y.ticks,
    font: {
      family: systemUiFontFamily
    }
  }
  clone.scales.x.ticks = {
    ...clone.scales.x.ticks,
    font: {
      family: systemUiFontFamily
    }
  }

  return clone
}

const getDashboardPreviewChartOptions = ({
  chartLines,
  fullSizeChart,
  aspectRatio,
  splitEnabled
}) => {
  const clone = cloneDeep(
    getDetailedNextChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio
    })
  )
  const minMaxOptions = fullSizeChart
    ? getMinMaxOptionsForFullSizeChart(chartLines, true, true)
    : getMinMaxOptions(chartLines, fullSizeChart)

  clone.plugins.tooltip = null
  clone.elements.line.borderWidth = splitEnabled ? 2 : 6
  clone.pointRadius = splitEnabled ? 3 : 4.5
  clone.pointBorderWidth = splitEnabled ? 0 : 3
  clone.scales.y.grid = {
    ...clone.scales.y.grid,
    borderDash: [4, 2],
    tickLength: 0,
    color: DEFAULT_GRID_COLOR,
    drawBorder: false
  }
  clone.scales.y = {
    ...clone.scales.y,
    position: 'right',
    offset: true
  }
  clone.scales.x = {
    ...clone.scales.x,
    offset: true
    // time: {
    //   parser: value => {
    //     return dayjs(value).format('YYYY')
    //   }
    // },
    // adapters: {
    //   date: dayjs
    // }
  }

  clone.scales.x.border = {
    display: false
  }

  clone.scales.y.ticks = {
    ...clone.scales.y.ticks,
    padding: 8,
    paddingRight: 0,
    font: {
      size: 10,
      family: systemUiFontFamily
    },
    callback: value => {
      return `${getSimplifiedNumber(value, true)[PLURALIZATION_KEYS.SIMPLIFIED]}${
        MATH_SYMBOLS.PERCENT
      }`
    }
  }
  clone.scales.x.ticks = {
    ...clone.scales.x.ticks,
    autoSkip: true,
    format: 'dates',
    maxRotation: 0,
    font: {
      size: 12,
      family: systemUiFontFamily
    },
    callback: (value, index, items) => {
      // show first, median and last label
      const medianIndex = Math.floor(items.length / 2)
      // return index === 0 || index === items.length - 1 ? value : null
      return index === 0 || index === medianIndex || index === items.length - 1 ? value : null
    }
  }

  clone.scales.x.grid = {
    ...clone.scales.x.grid,
    color: DEFAULT_GRID_COLOR,
    tickLength: 0,
    drawTicks: true,
    drawBorder: false
  }

  return merge(clone, minMaxOptions)
}

const getHomepagePreviewChartOptions = ({ chartLines, fullSizeChart, aspectRatio }) => {
  const clone = cloneDeep(
    getDetailedNextChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio
    })
  )
  const minMaxOptions = fullSizeChart
    ? getMinMaxOptionsForFullSizeChart(chartLines, true, true)
    : getMinMaxOptions(chartLines, fullSizeChart)

  clone.elements.line.borderWidth = 4
  clone.pointRadius = 6
  clone.pointHoverRadius = 6
  clone.pointBorderWidth = 2
  clone.pointHoverBorderWidth = 2
  clone.pointHitRadius = 6
  clone.pointBorderColor = TRANSPARENT_COLOR
  clone.pointHoverBorderColor = WHITE_COLOR

  clone.interaction = {
    ...TOOLTIP_INTERACTION_MODE_SETTINGS
  }

  const getGridLineWidth = ctx => {
    // calculation using indexes is not suitable for last grid line
    // because grid lines can be hidden by ticks
    // and indexes can be even or odd or skipped
    // so we probably need to use ticks values
    // and check for max and min values
    const { index, tick, chart } = ctx
    if (!chart) {
      return 1
    }

    const { value } = tick

    const allGridLinesValues = chart.scales.y.ticks.map(t => t.value)

    const maxGridLineValue = Math.max(...allGridLinesValues)

    return !index || value >= maxGridLineValue ? 2 : 1 // first and last grid lines are bold
  }

  const { y } = clone.scales

  clone.scales.y = {
    ...y,
    position: 'left',
    // offset: true
    grid: {
      ...y.grid,
      borderDash: null,
      tickLength: 11, // space between tick and grid line
      color: GREY_3_NEXT_COLOR,
      lineWidth: ctx => getGridLineWidth(ctx),
      drawBorder: false
    },
    ticks: {
      padding: 0,
      color: GREY_1_NEXT_COLOR,
      align: TICKS_ALIGNS.INNER,
      crossAlign: 'far', // https://www.chartjs.org/docs/latest/axes/cartesian/#tick-alignment
      font: FONT_SETTINGS,
      callback: value => getSimplifiedYTick({ value, unit: MATH_SYMBOLS.PERCENT })
    }
  }

  clone.scales.x.grid = {
    ...clone.scales.x.grid,
    display: false,
    tickLength: 6, // space between tick and grid line
    drawBorder: false
  }

  clone.scales.x.ticks = {
    ...clone.scales.x.ticks,
    autoSkip: true,
    format: 'dates',
    color: GREY_1_NEXT_COLOR,
    maxRotation: 0,
    align: 'inner', // works with reverse logic as y.ticks (text align for ticks)
    // crossAlign: 'center', // text align for ticks (vertical align)
    // https://www.chartjs.org/docs/latest/api/interfaces/TickOptions.html
    font: FONT_SETTINGS
  }

  clone.plugins.tooltip = {
    ...getStyledTooltipSettings({
      yMax: minMaxOptions.scales.y.max,
      unit: MATH_SYMBOLS.PERCENT
    })
  }

  return merge(clone, minMaxOptions)
}

const getElementProgressChartOptions = ({
  chartLines,
  fullSizeChart,
  aspectRatio,
  scales = {},
  xGranulationId = null,
  isPlug,
  additionalChartConfiguration = {},
  drawTooltipVerticalLine = false
}) => {
  const clone = cloneDeep(
    getDetailedNextChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio
    })
  )

  clone.elements.line.borderWidth = 3
  clone.pointRadius = 5
  clone.pointHoverRadius = 6
  clone.pointBorderWidth = 2
  clone.pointHoverBorderWidth = 2
  clone.pointHitRadius = 30
  clone.pointBorderColor = WHITE_COLOR
  clone.pointHoverBorderColor = WHITE_COLOR

  const { y } = clone.scales

  const predefinedYTicks = scales.y || []

  if (!isEmpty(predefinedYTicks)) {
    clone.scales.y.max = Math.max(...predefinedYTicks)

    const min = Math.min(...predefinedYTicks)

    const param = min === 0 ? 'min' : 'suggestedMin'

    clone.scales.y[param] = Math.min(...predefinedYTicks)
  }

  const yUnit = scales.yUnit || ''
  // add space before unit if it's not a percentage
  const resolvedUnit = getResolvedUnitForChart(yUnit)

  const tickColor = isPlug ? TRANSPARENT_COLOR : DARK_3_COLOR

  clone.scales.y = {
    ...y,
    position: 'left',
    // offset: true
    grid: {
      ...y.grid,
      borderDash: null,
      tickLength: 18, // space between tick and grid line
      color: GREY_3_NEXT_COLOR,
      drawBorder: false,
      z: -2 // make y grid lines behind the x grid lines
    },
    ticks: {
      // stepSize: calculateYAxesStepSize(clone.scales.y.max),
      // autoSkip: true,
      // maxTicksLimit: 6,
      padding: 0,
      color: tickColor,
      align: TICKS_ALIGNS.INNER,
      crossAlign: 'near', // https://www.chartjs.org/docs/latest/axes/cartesian/#tick-alignment
      font: FONT_SETTINGS,
      callback: value => {
        return getSimplifiedYTick({ value, unit: resolvedUnit, skipEvenPoints: false })
      }
    }
  }

  //const getGridLineColor = ctx => {
  //  const { tick, chart } = ctx
  //
  //  if (!chart || !tick || !tick.value) return GREY_3_NEXT_COLOR
  //
  //  const COMPARE_UNITS = {
  //    [PERIODS.WEEKLY.value]: DATE_UNITS.WEEK,
  //    [PERIODS.MONTHLY.value]: DATE_UNITS.MONTH,
  //    [PERIODS.QUARTERLY.value]: DATE_UNITS.QUARTER,
  //    [PERIODS.YEARS.value]: DATE_UNITS.YEAR
  //  }
  //
  //  if (!COMPARE_UNITS[xGranulationId]) return GREY_3_NEXT_COLOR
  //
  //  const today = dayjs()
  //
  //  const allGridLinesValues = chart.scales.x.ticks.map(t => t.value)
  //
  //  // highlight only first compatible grid line
  //  const firstCompatibleGridLine = allGridLinesValues.find(value => {
  //    const tickDate = dayjs(value)
  //    return today.isSame(tickDate, COMPARE_UNITS[xGranulationId])
  //  })
  //
  //  if (!firstCompatibleGridLine) return GREY_3_NEXT_COLOR
  //
  //  const shouldHighlightLine = firstCompatibleGridLine === tick.value
  //
  //  return shouldHighlightLine ? '#172B4D26' : GREY_3_NEXT_COLOR
  //}

  //const getGridLineColor = ctx => {
  //  const { tick, chart } = ctx
  //
  //  if (!chart || !tick || !tick.value) return GREY_3_NEXT_COLOR
  //
  //  const today = dayjs(1742283908005)
  //
  //  const allGridLinesValues = chart.scales.x.ticks.map(t => t.value)
  //
  //  // highlight today grid line
  //  const todayGridLine = allGridLinesValues.findLast(value => {
  //    const tickDate = dayjs(value)
  //    return today.isSame(tickDate, 'day')
  //  })
  //
  //  if (!todayGridLine) return GREY_3_NEXT_COLOR
  //
  //  const shouldHighlightLine = todayGridLine === tick.value
  //
  //  return shouldHighlightLine ? '#172B4D26' : GREY_3_NEXT_COLOR
  //}

  clone.scales.x.grid = {
    ...clone.scales.x.grid,
    tickLength: 12, // space between tick and grid line
    drawBorder: false,
    color: GREY_3_NEXT_COLOR
    // color: ctx => getGridLineColor(ctx)
  }

  const AVAILABLE_GRANULATION = [
    PERIODS.WEEKLY.value,
    PERIODS.MONTHLY.value,
    PERIODS.QUARTERLY.value,
    PERIODS.YEARS.value
  ]

  clone.scales.x.ticks = {
    ...clone.scales.x.ticks,
    autoSkip: true,
    format: 'dates',
    color: tickColor,
    // maxRotation: 0,
    align: 'inner', // works with reverse logic as y.ticks (text align for ticks)
    // crossAlign: 'center', // text align for ticks (vertical align)
    // https://www.chartjs.org/docs/latest/api/interfaces/TickOptions.html
    font: FONT_SETTINGS,
    callback: (label, index, items) => {
      if (xGranulationId && AVAILABLE_GRANULATION.includes(xGranulationId) && [items[index]]) {
        const { value } = items[index]

        const date = dayjs(value)

        const formattedLabels = {
          [PERIODS.WEEKLY.value]: date.format(MILESTONE_DAY_FORMAT),
          [PERIODS.MONTHLY.value]: date.format(MILESTONE_MONTH_FORMAT),
          [PERIODS.QUARTERLY.value]: getLabelForQuarter(value),
          [PERIODS.YEARS.value]: date.format('YYYY')
        }

        return formattedLabels[xGranulationId] || label
      }

      return label
    }
  }

  const { callbacks, filter } = DEFAULT_TOOLTIP_OPTIONS

  const tooltipStyles = getStyledTooltipSettings({
    yMax: clone.scales.y.max,
    unit: resolvedUnit
  })

  const { bodyFont, titleFont } = tooltipStyles

  clone.plugins.tooltip = {
    enabled: true,
    callbacks,
    filter,
    ...tooltipStyles,
    caretPadding: 8,
    cornerRadius: 6,
    titleMarginBottom: 0,
    titleFont: {
      ...titleFont,
      size: 10
    },
    bodyFont: {
      ...bodyFont,
      size: 16,
      lineHeight: 1.5
    },
    padding: {
      x: 12,
      top: 4,
      bottom: 0
    },
    animation: false
  }

  if (drawTooltipVerticalLine) {
    clone.plugins.tooltip = {
      ...clone.plugins.tooltip,
      yAlign: 'top',
      position: PROGRESS_CHART_TOOLTIP_POSITIONER
    }
  }

  return merge(clone, {
    ...additionalChartConfiguration
  })
}

const getPredictedScoreChartOptions = ({
  chartLines,
  fullSizeChart,
  aspectRatio,
  setYFrom0To100,
  currentGrade = 0,
  predictedScore = 0
}) => {
  const defaultOptions = getDefaultOptions(chartLines, fullSizeChart, aspectRatio, setYFrom0To100)

  const clone = cloneDeep(
    getHomepagePreviewChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio
    })
  )

  const options = {
    pointRadius: 5,
    pointHoverRadius: 6,
    pointBorderWidth: 3,
    pointHoverBorderWidth: 3,
    scales: {
      x: {
        grid: {
          ...clone.scales.x.grid,
          drawBorder: true
        },
        ticks: {
          ...clone.scales.x.ticks,
          callback: (value, index, items) => {
            // show first and last label
            return index === 0 || index === items.length - 1 ? value : null
          }
        }
      },
      y: {
        ...clone.scales.y,
        max: 110,
        grid: {
          ...clone.scales.y.grid,
          display: false
        },
        ticks: {
          ...clone.scales.y.ticks,
          stepSize: 1,

          callback: value => {
            return value === currentGrade || value === predictedScore
              ? `${getSimplifiedNumber(value, true)[PLURALIZATION_KEYS.SIMPLIFIED]}${
                  MATH_SYMBOLS.PERCENT
                }`
              : null
          },
          display: false
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      }
    }
  }

  return merge(defaultOptions, options)
}

const calculateYAxesStepSize = yMax => {
  const [stepSize] = [yMax <= 1e2 && 10, yMax <= 1e4 && 100, 1e3].filter(Boolean)
  return stepSize
}

export const prepareChart = ({
  chartLines,
  targetLines = [],
  targetLinesOptions = { fill: false },
  chartType = CHART_TYPES.simple,
  splitEnabled = false,
  fullSizeChart = false,
  aspectRatio = 2.4,
  setYFrom0To100 = false,
  pointBackgroundColor = null,
  pointHoverBackgroundColor = null,
  timeIntervalsCount = TIME_INTERVALS_COUNT,
  isLinear = false,
  dateFormat = 'DD/MMM',
  tableData = [],
  granulation,
  gradeValues = {
    thresholdBehind: THRESHOLDS_DEFAULT_VALUES.BEHIND,
    thresholdOnTrack: THRESHOLDS_DEFAULT_VALUES.ON_TRACK,
    currentGrade: 0,
    predictedScore: 0,
    fillThresholdLines: false
  },
  tension = 0.4,
  scales = {},
  xGranulationId = null,
  isPlug = false,
  additionalChartConfiguration = {},
  drawTooltipVerticalLine = false
}) => {
  const labels = prepareLabels({
    chartLines,
    fullSizeChart,
    timeIntervalsCount,
    granulation,
    scales
  })

  const linear = prepareLinear(chartLines)

  const { thresholdBehind, thresholdOnTrack, currentGrade, predictedScore, fillThresholdLines } =
    gradeValues

  let thresholdLines = []

  if (chartType === CHART_TYPES.predictedScorePreview) {
    thresholdLines = prepareThresholds({
      thresholdBehind,
      thresholdOnTrack,
      chartLines,
      fillThresholdLines
    })
  }

  const datasets = prepareDatasets(
    chartLines,
    splitEnabled,
    pointBackgroundColor,
    pointHoverBackgroundColor,
    tableData,
    tension,
    targetLines,
    chartType,
    targetLinesOptions
  )

  if (isLinear && !splitEnabled) {
    datasets.push(linear)
  }

  if (!isEmpty(thresholdLines)) {
    datasets.push(...thresholdLines)
  }

  const {
    detailed,
    simple,
    simpleNext,
    detailedNext,
    dashboardPreview,
    homepagePreview,
    predictedScorePreview,
    elementProgress
  } = CHART_TYPES
  const optionsByChartType = {
    [detailed]: getDetailedChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      dateFormat
    }),
    [detailedNext]: getDetailedNextChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      dateFormat,
      splitEnabled
    }),
    [simple]: getSimpleChartOptions({
      chartLines,
      fullSizeChart,
      splitEnabled,
      aspectRatio,
      setYFrom0To100
    }),
    [simpleNext]: getSimpleNextChartOptions({
      chartLines,
      fullSizeChart,
      splitEnabled,
      aspectRatio,
      setYFrom0To100
    }),
    [dashboardPreview]: getDashboardPreviewChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      dateFormat,
      splitEnabled
    }),
    [homepagePreview]: getHomepagePreviewChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      dateFormat,
      splitEnabled
    }),
    [elementProgress]: getElementProgressChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      dateFormat,
      splitEnabled,
      scales,
      xGranulationId,
      isPlug,
      additionalChartConfiguration,
      drawTooltipVerticalLine
    }),
    [predictedScorePreview]: getPredictedScoreChartOptions({
      chartLines,
      fullSizeChart,
      aspectRatio,
      setYFrom0To100,
      currentGrade,
      predictedScore
    })
  }

  const options = optionsByChartType[chartType]

  return {
    labels,
    datasets,
    options
  }
}

export const prepareTable = source => {
  const columns = prepareColumns(source.tableData.columns, source.tableData.granulation)
  const rows = prepareRows(source)

  return {
    columns,
    rows
  }
}

export const isRefreshChartDataNeed = ({ oldValue, newValue, showChart }) => {
  const { GRADE, DUE_DATE, ELEMENT_START_DATE, ID, INTERVAL_ID, EXIST_CONTRIBUTE_CHILDREN } =
    OKR_ELEMENT_KEYS
  return (
    showChart &&
    [GRADE, DUE_DATE, ELEMENT_START_DATE, ID, INTERVAL_ID, EXIST_CONTRIBUTE_CHILDREN].some(
      key => oldValue[key] !== newValue[key]
    )
  )
}
